import { observer } from 'mobx-react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { wizardStore } from '@/store/wizard.store';
import { PHASE_INTRO } from './ConfiguratorIntro';

const PhaseChevron = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'completed',
})<{ completed: boolean }>(({ theme, completed }) => ({
  flex: 1,
  minWidth: 180,
  padding: theme.spacing(2.5, 2),
  borderRadius: theme.shape.borderRadius,
  border: `1px ${completed ? 'solid' : 'dashed'}`,
  borderColor: completed ? theme.palette.secondary.main : theme.palette.divider,
  backgroundColor: completed ? 'rgba(118,184,45,0.06)' : theme.palette.background.paper,
}));

/**
 * Schermata di transizione tra fasi (port del legacy PhaseListPage in modalità "fase conclusa"):
 * conferma la fase appena completata + elenco fasi con stato + "PASSA ALLA FASE SUCCESSIVA".
 */
export const PhaseTransition = observer(function PhaseTransition() {
  const transition = wizardStore.phaseTransition;
  const phases = wizardStore.groupedMenu;
  const allSubsteps = wizardStore.menu;

  if (transition === null) return null;

  const isPhaseCompleted = (phaseKey: string): boolean => {
    const subs = allSubsteps.filter((s) => s.phaseKey === phaseKey);
    return subs.length > 0 && subs.every((s) => s.isAnswered);
  };

  const nextPhaseKey = allSubsteps.find((s) => s.substepId === transition.next.substepId)?.phaseKey;
  const nextPhaseName = phases.find((p) => p.phaseKey === nextPhaseKey)?.phaseName ?? 'la fase successiva';

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 4 }}>
        <CheckCircleIcon color="secondary" sx={{ fontSize: 48 }} aria-hidden="true" />
        <Typography sx={{ fontFamily: '"miller", serif', fontStyle: 'italic', fontSize: 24, lineHeight: 1.4, mt: 1 }}>
          Complimenti! Hai concluso la fase{' '}
          <Box component="span" sx={{ color: 'primary.main', textTransform: 'uppercase' }}>
            {transition.completedPhaseName}
          </Box>
          .
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Ora puoi passare a {nextPhaseName}.
        </Typography>

        <Button
          variant="contained"
          color="secondary"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={() => void wizardStore.continueToNextPhase()}
          sx={{ mt: 3, px: 5, py: 1.5 }}
        >
          Passa alla fase successiva
        </Button>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ maxWidth: 1100, mx: 'auto' }}>
        {phases.map((phase) => {
          const copy = PHASE_INTRO[phase.phaseKey];
          const completed = isPhaseCompleted(phase.phaseKey);
          const isNext = phase.phaseKey === nextPhaseKey;
          return (
            <PhaseChevron key={phase.phaseKey} completed={completed}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                <Typography variant="h6" sx={{ textTransform: 'uppercase', fontSize: '1rem' }}>
                  {copy?.title ?? phase.phaseName}
                </Typography>
                {completed && <CheckCircleIcon color="secondary" fontSize="small" aria-hidden="true" />}
              </Box>
              <Typography
                variant="overline"
                sx={{ letterSpacing: '0.06em', color: isNext ? 'primary.main' : 'text.secondary', fontWeight: isNext ? 700 : 400 }}
              >
                {completed ? 'Completata' : isNext ? 'Prossima fase' : 'Potrai personalizzare'}
              </Typography>
              {!completed && (
                <Stack component="ul" sx={{ pl: 2, m: 0, mt: 0.5 }} spacing={0.25}>
                  {(copy?.descriptions ?? []).map((d) => (
                    <Typography key={d} component="li" variant="body2" color="text.secondary">
                      {d}
                    </Typography>
                  ))}
                </Stack>
              )}
            </PhaseChevron>
          );
        })}
      </Stack>
    </Box>
  );
});
